<script lang="ts" setup>
import { computed, defineComponent, PropType } from 'vue'

import USAvatar from '@base/components/USAvatar/USAvatar.vue'
import USRatingStars from '@base/components/USRatingStars/USRatingStars.vue'
import USButtonMenu from '@base/components/USButtonMenu/USButtonMenu.vue'
import ImageThumbnail from '@base/components/ImageThumbnail/ImageThumbnail.vue'
import TagPill from '@base/components/TagPill/TagPill.vue'

import { ReviewCard } from '@base/_types/ReviewCard'

defineComponent({
  name: 'ReviewCard'
})

const props = defineProps({
  review: {
    type: Object as PropType<ReviewCard>,
    default: () => null
  }
})

const emit = defineEmits(['dropdownMenu'])

let handleClickOption = (action: string, id: string, username: string) => {
  emit('dropdownMenu', { action, id, username })
}

const bookedDateConverted = computed(() => {
  const day = props.review.bookedDate.getDay()
  const month = props.review.bookedDate.getMonth()
  const year = props.review.bookedDate.getFullYear()
  return `${day}/${month}/${year}`
})

const responseDateConverted = computed(() => {
  const day = props.review.responseDate.getDay()
  const month = props.review.responseDate.getMonth()
  const year = props.review.responseDate.getFullYear()
  return `${day}/${month}/${year}`
})

const userInitials = computed(
  () =>
    props.review.user.firstName.charAt(0) + props.review.user.lastName.charAt(0)
)
</script>

<template>
  <div data-test="review-card" class="review-card">
    <div class="review-card__user-review grid__row">
      <div
        class="review-card__user grid__column grid__column--12 grid__column--breakpoint--7--3 grid__column--breakpoint--13--2"
      >
        <USAvatar
          :src="review.user.avatar || ''"
          :initials="
            props.review.user.firstName && props.review.user.lastName
              ? userInitials
              : undefined
          "
          class="review-card__avatar"
        />
        <p class="review-card__username display--d2--semibold">
          {{ review.user.username }}
        </p>
        <router-link
          v-if="review.showBookingId && review.bookingId && review.bookingUrl"
          :to="review.bookingUrl"
          class="review-card__bookingId body--p1--medium"
        >
          #{{ review.bookingId }}
        </router-link>
        <p class="review-card__date body--p2--regular">
          {{ bookedDateConverted }}
        </p>
        <USRatingStars :filled="review.rating" :stars="5" :readonly="true" />
      </div>
      <div
        class="review-card__review-wrapper grid__column grid__column--12 grid__column--breakpoint--7--9"
      >
        <div v-if="review.tags" class="review-card__tags">
          <!-- TODO this has to be replaced with the TagFilter -->
          <div v-for="tag in review.tags" :key="tag" class="review-card__tag">
            <TagPill
              :id="tag"
              :label="tag"
              :model-value="true"
              :name="tag"
              :value="tag"
            />
          </div>
        </div>
        <p class="review-card__review body--p2--regular">{{ review.review }}</p>
        <div v-if="review.images" class="review-card__images">
          <ImageThumbnail
            v-for="image in review.images"
            :key="image"
            class="review-card__image"
            :src="image.src"
          />
        </div>
      </div>

      <USButtonMenu
        v-if="review.showDropdown"
        class="review-card__dropdown"
        :dropdown-menu-items="review.dropdownOptions"
        icon-color="var(--color--primary-b1)"
        dropdown-menu-alignment="right"
        @clickOption="
          handleClickOption($event, review.reviewId, review.user.username)
        "
      />
    </div>
    <div
      v-if="review.response && review.responseAuthor"
      class="review-card__response grid__row"
    >
      <div
        class="review-card__user grid__column grid__column--12 grid__column--breakpoint--7--3 grid__column--breakpoint--13--2"
      >
        <USAvatar
          :src="review.responseAuthor.avatar || ''"
          class="review-card__avatar"
        />
        <p class="review-card__username display--d2--semibold">
          {{ review.responseAuthor.username }}
        </p>
        <p class="review-pending-card__date body--p2--regular">
          {{ responseDateConverted }}
        </p>
      </div>
      <div
        class="review-card__review-wrapper grid__column grid__column--12 grid__column--breakpoint--7--9"
      >
        <p class="review-card__review body--p2--regular">
          {{ review.response }}
        </p>
      </div>
      <USButtonMenu
        v-if="review.showResponseDropdown"
        class="review-card__dropdown"
        :dropdown-menu-items="review.responseDropdownOptions"
        icon-color="var(--color--primary-b1)"
        dropdown-menu-alignment="right"
        @clickOption="
          handleClickOption($event, review.responseId, review.responseAuthor)
        "
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';

.review-card {
  border-bottom: var(--line-weight--light) solid var(--color--neutral-a4);
  border-top: var(--line-weight--light) solid var(--color--neutral-a4);
  color: var(--color--neutral-a1);
  padding-bottom: var(--spacing--7);
  padding-top: var(--spacing--7);

  &__user-review {
    display: flex;
    flex-flow: column nowrap;

    @media (min-width: breakpoint.$breakpoint--tablet) {
      align-items: flex-start;
      flex-flow: row;
    }
  }

  &__user {
    align-items: center;
    display: flex;
    flex-flow: column nowrap;
    padding-bottom: var(--spacing--6);

    @media (min-width: breakpoint.$breakpoint--tablet) {
      align-items: flex-start;
      padding-bottom: 0;
    }
  }

  &__username {
    margin-top: var(--spacing--9);
  }

  &__bookingId {
    color: var(--color--primary-b1);
    font-weight: var(--font-weight--semibold);
    word-break: break-word;
  }

  &__date {
    margin-bottom: var(--spacing--9);
    margin-top: var(--spacing--9);
  }

  &__tags {
    display: flex;
    flex-flow: row;
    justify-content: center;

    @media (min-width: breakpoint.$breakpoint--tablet) {
      justify-content: flex-start;
    }
  }

  &__tag {
    margin-left: var(--spacing--9);
  }

  &__review {
    margin-top: var(--spacing--7);
  }

  &__images {
    display: flex;
    flex-flow: row wrap;
  }

  &__image {
    flex-basis: 33.3%;
    margin-top: var(--spacing--6);

    @media (min-width: breakpoint.$breakpoint--tablet) {
      flex-basis: 25%;
      margin-right: var(--spacing--6);
    }

    @media (min-width: breakpoint.$breakpoint--desktop) {
      margin-right: 0;
    }
  }

  &__dropdown {
    position: absolute;
    right: 0;
    top: 0;
  }

  &__response {
    margin-top: var(--spacing--6);
  }
}

:deep(.us-rating-stars__star) {
  --iconDiameter: 20px;

  &:not(:first-child) {
    margin-left: var(--spacing--9);
  }
}
</style>
